import React from 'react';
import classNames from 'classnames';
import type {
  Instrument,
  TFormatAmountExtendedPart,
  TFormatAmountPart,
} from '@tokenized/sdk-js-private';

export type TInstrumentAmountPart = TFormatAmountPart & {
  key: string;
  className?: string;
  defaultRender: JSX.Element;
};

/** @category React Components */
export function InstrumentAmount<TRenderAsComponent extends React.ElementType>({
  instrument,
  renderAs,
  renderAsElementProps,
  className,
  partsRender,
  hideCurrency,
  showCurrencyCodeOnRight,
  allowCurrencySymbolAndCode,
  showDisplayCurrency,
  showDisplayCurrencyWhenSame,
  useCustomPrecisionForDisplayCurrency = false,
  showUnconvertibleAmountAsZero,
  showWhenZero = true,
  numberFormatOverrides,
  splitLongFractions = true,
  enforceCompleteFractionGroups = false,
  increasePrecisionForTinyNumbers = false,
  typographicMinusSigns = true,
  spaceAroundSigns = true,
  forceSpaceAroundCurrency = true,
}: {
  instrument?: Instrument;
  renderAs?: TRenderAsComponent;
  renderAsElementProps?: React.ComponentProps<TRenderAsComponent>;
  className?: string;
  partsRender?: (part: TInstrumentAmountPart) => React.ReactNode;
  hideCurrency?: boolean;
  showCurrencyCodeOnRight?: boolean;
  allowCurrencySymbolAndCode?: boolean;
  showDisplayCurrency?: boolean;
  showDisplayCurrencyWhenSame?: boolean;
  useCustomPrecisionForDisplayCurrency?: boolean;
  showUnconvertibleAmountAsZero?: boolean;
  showWhenZero?: boolean;
  numberFormatOverrides?: Intl.NumberFormatOptions;
  splitLongFractions?: boolean;
  enforceCompleteFractionGroups?: boolean;
  increasePrecisionForTinyNumbers?: boolean;
  typographicMinusSigns?: boolean;
  spaceAroundSigns?: boolean;
  forceSpaceAroundCurrency?: boolean;
}) {
  const Element = renderAs || 'span';

  const formatted = instrument?.formatAmountParts?.({
    hideCurrency,
    showCurrencyCodeOnRight,
    allowCurrencySymbolAndCode,
    showDisplayCurrency,
    showDisplayCurrencyWhenSame,
    showUnconvertibleAmountAsZero,
    useCustomPrecisionForDisplayCurrency,
    showWhenZero,
    numberFormatOverrides,
    splitLongFractions,
    enforceCompleteFractionGroups,
    increasePrecisionForTinyNumbers,
    typographicMinusSigns,
    spaceAroundSigns,
    forceSpaceAroundCurrency,
  });
  if (!formatted) {
    return null;
  }
  const { parts, number } = formatted;

  return (
    <Element
      {...renderAsElementProps}
      className={classNames(
        'instrument-quantity',
        number < 0 && 'quantity-negative',
        className,
      )}
    >
      {parts.map((part, index) => {
        const key = `${part.type}${index}`;
        const className =
          [
            part.type ? `quantity-part-${part.type}` : false,
            (part as TFormatAmountExtendedPart).subType
              ? `quantity-part-${(part as TFormatAmountExtendedPart).subType}`
              : false,
          ]
            .filter(Boolean)
            .join(' ') || undefined;

        const defaultRender = (
          <span key={key} className={className}>
            {part.value}
          </span>
        );
        if (partsRender) {
          return partsRender({
            ...part,
            key,
            className,
            defaultRender,
          });
        }
        return defaultRender;
      })}
    </Element>
  );
}
